import React, { useState } from 'react';
import PropTypes from 'prop-types';

const NoteViewer = ({ note, mode = 'compact', formats = null, onConvertFormat = null, convertStatus = 'idle', convertError = null, allowRegenerate = true }) => {
  const [isExpanded, setIsExpanded] = useState(mode === 'full');
  const [activeTab, setActiveTab] = useState('plain_text');
  const [showRawHtml, setShowRawHtml] = useState(false);
  const [copiedField, setCopiedField] = useState(null);

  const copyToClipboard = async (text, fieldId) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldId);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      alert('Failed to copy to clipboard');
    }
  };

  const CopyButton = ({ text, fieldId }) => (
    <button className="btn btn-sm btn-outline-secondary" onClick={() => copyToClipboard(text, fieldId)} title="Copy to clipboard" disabled={!text}>
      <i className={`fas ${copiedField === fieldId ? 'fa-check text-success' : 'fa-copy'} me-1`}></i>
      {copiedField === fieldId ? 'Copied!' : ''}
    </button>
  );

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', timeZoneName: 'short' });
  };

  const toggleExpand = () => mode === 'compact' && setIsExpanded(!isExpanded);
  const showTabs = mode === 'full' || (mode === 'compact' && isExpanded);

  // Strip HTML tags for plain text display
  const stripHtml = (html) => {
    const tmp = document.createElement('DIV');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  // Extract text from note content (handles both string and object formats)
  const extractTextFromContent = (content) => {
    if (!content) return '';
    // If content is a string, strip HTML
    if (typeof content === 'string') {
      return stripHtml(content);
    }
    // If content is an object (structured_content), extract text from sections
    if (typeof content === 'object') {
      const parts = [];
      for (const [key, value] of Object.entries(content)) {
        if (value) {
          // Format the section name (convert snake_case to Title Case)
          const sectionName = key.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
          // If section has a content field, use it
          if (typeof value === 'object' && value.content) {
            parts.push(`${sectionName}:\n${value.content}`);
          } else if (typeof value === 'string') {
            parts.push(`${sectionName}:\n${value}`);
          }
        }
      }
      return parts.join('\n\n');
    }
    return '';
  };

  // Prefer pre-formatted plain_text from formats, fallback to extracting from content
  const plainText = formats?.plain_text || extractTextFromContent(note.content);
  const shouldTruncate = mode === 'compact' && !isExpanded && plainText.length > 500;

  // Always show all tabs like sandbox
  const tabs = [
    { key: 'json', label: 'Raw JSON' },
    { key: 'html', label: 'HTML' },
    { key: 'plain_text', label: 'Plain Text' },
    { key: 'rtf', label: 'RTF' }
  ];

  return (
    <div className={`border rounded p-3 ${mode === 'compact' ? 'mb-3' : ''}`}>
      <div className="d-flex justify-content-between align-items-start mb-2">
        <div className="flex-grow-1">
          <div className="d-flex align-items-center gap-2 mb-1">
            <h6 className="mb-0 text-primary">
              {note.template?.name || 'Note'}
            </h6>
            {note.is_latest && <span className="badge bg-success">Latest</span>}
            <span className="badge bg-secondary">v{note.version || '1'}</span>
          </div>
          <div className="d-flex gap-3 small text-muted mb-1">
            <span><i className="far fa-clock me-1"></i>{formatDate(note.created_at)}</span>
            {note.verbosity_setting && <span><i className="fas fa-sliders-h me-1"></i>{note.verbosity_setting}</span>}
            {note.section_type && <span><i className="fas fa-file-alt me-1"></i>{note.section_type}</span>}
          </div>
          {mode === 'compact' && !isExpanded && (
            <div className="text-muted small mt-2" style={{
              maxHeight: '3em',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical'
            }}>
              {plainText.substring(0, 200)}...
            </div>
          )}
        </div>
        {mode === 'compact' && (
          <button
            className="btn btn-sm btn-outline-primary"
            onClick={toggleExpand}
            style={{ minWidth: '80px' }}
          >
            <i className={`fas fa-${isExpanded ? 'eye-slash' : 'eye'} me-1`}></i>
            {isExpanded ? 'Hide' : 'View'}
          </button>
        )}
      </div>

      {isExpanded && (
        <>

          {convertStatus === 'error' && convertError && (
            <div className="alert alert-danger alert-dismissible fade show py-2 mb-3" role="alert">
              <i className="fas fa-exclamation-circle me-1"></i>{convertError.error || 'Format conversion failed'}
            </div>
          )}

          {showTabs && tabs.length > 1 && (
            <ul className="nav nav-tabs mb-3">
              {tabs.map(tab => (
                <li className="nav-item" key={tab.key}>
                  <button className={`nav-link ${activeTab === tab.key ? 'active' : ''}`} onClick={() => setActiveTab(tab.key)}>{tab.label}</button>
                </li>
              ))}
            </ul>
          )}

          <div className="tab-content" style={{ maxHeight: mode === 'full' ? '600px' : '400px', overflowY: 'auto' }}>
            {activeTab === 'json' && (
              <div>
                <div className="d-flex justify-content-end mb-2">
                  <CopyButton text={JSON.stringify(formats?.json || note, null, 2)} fieldId={`json-${note.id}`} />
                </div>
                <pre className="bg-dark text-light p-3 rounded">{JSON.stringify(formats?.json || note, null, 2)}</pre>
              </div>
            )}

            {activeTab === 'html' && (
              <div>
                <div className="d-flex justify-content-end mb-2">
                  {(allowRegenerate || !formats?.html) && (
                    <button className="btn btn-sm btn-outline-primary me-2" onClick={() => onConvertFormat('html')} disabled={convertStatus === 'loading'}>
                      {convertStatus === 'loading' ? <><span className="spinner-border spinner-border-sm me-1" />Generating...</> : <><i className="fas fa-sync-alt me-1"></i>{!formats?.html ? 'Generate' : 'Regenerate'} HTML</>}
                    </button>
                  )}
                  {formats?.html && (
                    <>
                      <button className="btn btn-sm btn-outline-secondary me-2" onClick={() => setShowRawHtml(!showRawHtml)}>
                        <i className={`fas ${showRawHtml ? 'fa-eye' : 'fa-code'} me-1`}></i>{showRawHtml ? 'Show Formatted' : 'Show Raw HTML'}
                      </button>
                      <CopyButton text={formats.html} fieldId={`html-${note.id}`} />
                    </>
                  )}
                </div>
                {formats?.html ? (
                  showRawHtml ? (
                    <pre className="border rounded p-3 bg-dark text-light" style={{ maxHeight: '500px', overflowY: 'auto' }}><code>{formats.html}</code></pre>
                  ) : (
                    <iframe title="Rendered HTML Note" className="border rounded" style={{ width: '100%', height: '500px', backgroundColor: 'white' }} srcDoc={formats.html} />
                  )
                ) : (
                  <div className="text-center text-muted p-4 border rounded">
                    <i className="fas fa-code mb-2" style={{ fontSize: '2rem' }}></i>
                    <p className="mb-0">HTML format not available</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'plain_text' && (
              <div>
                <div className="d-flex justify-content-end mb-2">
                  {(allowRegenerate || !formats?.plain_text) && (
                    <button className="btn btn-sm btn-outline-primary me-2" onClick={() => onConvertFormat('plain_text')} disabled={convertStatus === 'loading'}>
                      {convertStatus === 'loading' ? <><span className="spinner-border spinner-border-sm me-1" />Generating...</> : <><i className="fas fa-sync-alt me-1"></i>{!formats?.plain_text ? 'Generate' : 'Regenerate'} Plain Text</>}
                    </button>
                  )}
                  <CopyButton text={formats?.plain_text || plainText} fieldId={`plain-${note.id}`} />
                </div>
                <div className="border rounded p-3 bg-light" style={{ whiteSpace: 'pre-wrap' }}>
                  {shouldTruncate ? `${plainText.substring(0, 500)}...` : (formats?.plain_text || plainText)}
                </div>
              </div>
            )}

            {activeTab === 'rtf' && (
              <div>
                <div className="d-flex justify-content-end mb-2">
                  {(allowRegenerate || !formats?.rtf) && (
                    <button className="btn btn-sm btn-outline-primary me-2" onClick={() => onConvertFormat('rtf')} disabled={convertStatus === 'loading'}>
                      {convertStatus === 'loading' ? <><span className="spinner-border spinner-border-sm me-1" />Generating...</> : <><i className="fas fa-sync-alt me-1"></i>{!formats?.rtf ? 'Generate' : 'Regenerate'} RTF</>}
                    </button>
                  )}
                  {formats?.rtf && <CopyButton text={formats.rtf} fieldId={`rtf-${note.id}`} />}
                </div>
                {formats?.rtf ? (
                  <pre className="border rounded p-3 bg-dark text-light" style={{ maxHeight: '500px', overflowY: 'auto', fontSize: '0.85rem' }}>{formats.rtf}</pre>
                ) : (
                  <div className="text-center text-muted p-4 border rounded">
                    <i className="fas fa-file-alt mb-2" style={{ fontSize: '2rem' }}></i>
                    <p className="mb-0">RTF format not available</p>
                  </div>
                )}
              </div>
            )}

            {!showTabs && <div style={{ whiteSpace: 'pre-wrap' }}>{shouldTruncate ? `${plainText.substring(0, 500)}...` : plainText}</div>}
          </div>

          {mode === 'compact' && !showTabs && shouldTruncate && (
            <button className="btn btn-sm btn-link p-0 mt-2" onClick={toggleExpand}>
              Show more <i className="fas fa-chevron-down ms-1"></i>
            </button>
          )}

          {note.feedback && (
            <div className="mt-3 p-2 bg-light rounded">
              <p className="mb-1"><strong><i className="fas fa-comment me-1"></i>Feedback:</strong></p>
              <p className="mb-0 small" style={{ whiteSpace: 'pre-wrap' }}>{note.feedback}</p>
            </div>
          )}

          <div className="d-flex flex-wrap gap-3 small text-muted border-top pt-2 mt-3">
            <span><i className="far fa-clock me-1"></i>{formatDate(note.created_at)}</span>
            {note.verbosity_setting && <span><i className="fas fa-sliders-h me-1"></i>Verbosity: {note.verbosity_setting}</span>}
            {note.section_type && <span><i className="fas fa-file-alt me-1"></i>Section: {note.section_type}</span>}
            {note.audio_transcript_id && <span><i className="fas fa-microphone me-1"></i>Has transcript</span>}
          </div>
        </>
      )}
    </div>
  );
};

NoteViewer.propTypes = {
  note: PropTypes.shape({ id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired, content: PropTypes.string, template: PropTypes.shape({ name: PropTypes.string, description: PropTypes.string }), created_at: PropTypes.string, version: PropTypes.number, is_latest: PropTypes.bool, feedback: PropTypes.string, verbosity_setting: PropTypes.string, section_type: PropTypes.string, audio_transcript_id: PropTypes.string }).isRequired,
  mode: PropTypes.oneOf(['compact', 'full']),
  formats: PropTypes.shape({ json: PropTypes.any, html: PropTypes.string, plain_text: PropTypes.string, rtf: PropTypes.string }),
  onConvertFormat: PropTypes.func,
  convertStatus: PropTypes.oneOf(['idle', 'loading', 'error']),
  convertError: PropTypes.shape({ error: PropTypes.string }),
  allowRegenerate: PropTypes.bool
};

export default NoteViewer;
